import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        // Store token and user info
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check if the backend server is running.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Admin Login - WeFlare"
        description="Admin login for WeFlare management system"
        robots="noindex, nofollow"
      />
      
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  {/* Logo/Header */}
                  <div className="text-center mb-4">
                    <img 
                      src="/logo.png" 
                      alt="WeFlare Logo" 
                      className="mb-3"
                      style={{ height: '60px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <h2 className="text-primary mb-2">Admin Login</h2>
                    <p className="text-muted">Access WeFlare Management System</p>
                  </div>

                  {/* Error Alert */}
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}

                  {/* Login Form */}
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label htmlFor="username" className="form-label">
                        <i className="bi bi-person me-2"></i>
                        Username
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        id="username"
                        name="username"
                        value={credentials.username}
                        onChange={handleInputChange}
                        placeholder="Enter your username"
                        required
                        autoComplete="username"
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="password" className="form-label">
                        <i className="bi bi-lock me-2"></i>
                        Password
                      </label>
                      <input
                        type="password"
                        className="form-control form-control-lg"
                        id="password"
                        name="password"
                        value={credentials.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100"
                      disabled={isLoading || !credentials.username || !credentials.password}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing in...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-box-arrow-in-right me-2"></i>
                          Sign In
                        </>
                      )}
                    </button>
                  </form>

                  {/* Footer */}
                  <div className="text-center mt-4">
                    <small className="text-muted">
                      <i className="bi bi-shield-check me-1"></i>
                      Secure admin access only
                    </small>
                  </div>
                </div>
              </div>

              {/* Development Info */}
              {process.env.NODE_ENV === 'development' && (
                <div className="card mt-3 border-warning">
                  <div className="card-body">
                    <h6 className="card-title text-warning">
                      <i className="bi bi-info-circle me-2"></i>
                      Development Mode
                    </h6>
                    <small className="text-muted">
                      Default credentials: <br/>
                      Username: <code>admin</code><br/>
                      Password: <code>admin123</code>
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;